import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import { globalErrorHandler } from '../error/saga';
import {
    buzzbridgeLoyaltyDashboardUrl,
    buzzbridgeLoyaltyInviteUrl,
    buzzbridgeLoyaltyMemberUrl,
    buzzbridgeLoyaltyNetworkUrl,
    buzzbridgeLoyaltyRedemptionCatalogUrl,
    buzzbridgeLoyaltyRedemptionRedeemUrl,
    buzzbridgeLoyaltyWalletUrl,
    buzzbridgeLoyaltyWithdrawUrl,
    buzzbridgeLoyaltyAdminWithdrawalsUrl,
    buzzbridgeLoyaltyAdminWithdrawalUrl,
    buzzbridgeLoyaltyAdminGrantUrl,
    buzzbridgeLoyaltyAdminOverviewUrl,
    buzzbridgeLoyaltyAdminLeaderboardUrl,
    buzzbridgeLoyaltyAdminUserUrl,
} from '../../configurations/api/url';
import { showToastMessage } from '../../utils/AppUtils';
import type { RootState } from '../root-reducer';
import {
    getLoyaltyDashboard,
    getLoyaltyDashboardFail,
    getLoyaltyDashboardSuccess,
    getLoyaltyInviteFail,
    getLoyaltyInviteSuccess,
    getLoyaltyMemberFail,
    getLoyaltyMemberSuccess,
    getLoyaltyNetworkFail,
    getLoyaltyNetworkSuccess,
    getLoyaltyWallet,
    getLoyaltyWalletFail,
    getLoyaltyWalletSuccess,
    getRedemptionCatalogFail,
    getRedemptionCatalogSuccess,
    redeemPointsFail,
    redeemPointsSuccess,
    requestLoyaltyWithdrawalFail,
    requestLoyaltyWithdrawalSuccess,
    getAdminLoyaltyWithdrawalsFail,
    getAdminLoyaltyWithdrawalsSuccess,
    patchAdminLoyaltyWithdrawalFail,
    patchAdminLoyaltyWithdrawalSuccess,
    getAdminLoyaltyOverviewSuccess,
    getAdminLoyaltyOverviewFail,
    getAdminLoyaltyLeaderboardSuccess,
    getAdminLoyaltyLeaderboardFail,
    getAdminLoyaltyUserSuccess,
    getAdminLoyaltyUserFail,
    adminGrantLoyaltyFail,
    adminGrantLoyaltySuccess,
    types,
    type IGetLoyaltyNetworkParams,
    type IGetAdminLoyaltyWithdrawalsParams,
    type IGetAdminLoyaltyLeaderboardParams,
    type IPatchAdminLoyaltyWithdrawalArgs,
    type IRedeemPointsPayload,
} from './actions';
import type {
    IBuzzbridgeLoyaltyWithdrawPayload,
    IBuzzbridgeLoyaltyWithdrawResult,
    IBuzzbridgeRedemptionResult,
} from '../../types/global/buzzbridgeLoyalty.types';
import type { IAdminLoyaltyGrantRequest } from '../../types/global/buzzbridgeLoyalty.types';
import { normalizeAdminLoyaltyLeaderboardResponse } from '../../utils/adminLoyaltyLeaderboard';

function buildNetworkQuery(params?: IGetLoyaltyNetworkParams): string {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.days) q.set('days', String(params.days));
    const qs = q.toString();
    return qs ? `?${qs}` : '';
}

function* getLoyaltyDashboardSaga(): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyDashboardUrl, httpRequest.GET, null);
        yield put(getLoyaltyDashboardSuccess(response.data));
    } catch (error: any) {
        yield put(getLoyaltyDashboardFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getLoyaltyNetworkSaga({ payload }: { payload?: IGetLoyaltyNetworkParams }): Generator<any, any, any> {
    try {
        const url = `${buzzbridgeLoyaltyNetworkUrl}${buildNetworkQuery(payload)}`;
        const response = yield call(api, url, httpRequest.GET, null);
        yield put(getLoyaltyNetworkSuccess(response.data));
    } catch (error: any) {
        yield put(getLoyaltyNetworkFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getLoyaltyMemberSaga({ payload }: { payload: string }): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyMemberUrl(payload), httpRequest.GET, null);
        yield put(getLoyaltyMemberSuccess(response.data));
    } catch (error: any) {
        yield put(getLoyaltyMemberFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getLoyaltyInviteSaga(): Generator<any, any, any> {
    try {
        const isLoggedIn: boolean = yield select((s: RootState) => Boolean(s.authentication?.isLoggedIn));
        if (!isLoggedIn) {
            yield put(getLoyaltyInviteFail({ message: 'Not authenticated' } as any));
            return;
        }
        const response = yield call(api, buzzbridgeLoyaltyInviteUrl, httpRequest.GET, null);
        yield put(getLoyaltyInviteSuccess(response.data));
    } catch (error: any) {
        yield put(getLoyaltyInviteFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getRedemptionCatalogSaga(): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyRedemptionCatalogUrl, httpRequest.GET, null);
        const programs = response.data?.programs ?? response.data ?? [];
        yield put(getRedemptionCatalogSuccess(Array.isArray(programs) ? programs : []));
    } catch (error: any) {
        yield put(getRedemptionCatalogFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* redeemPointsSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IRedeemPointsPayload;
    resolve?: (v: IBuzzbridgeRedemptionResult) => void;
    reject?: (e: unknown) => void;
}): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyRedemptionRedeemUrl, httpRequest.POST, payload);
        const redemption: IBuzzbridgeRedemptionResult = response.data?.redemption ?? response.data;
        yield put(redeemPointsSuccess(redemption));
        yield put(getLoyaltyDashboard());
        yield call(
            showToastMessage,
            `Redeemed ${redemption.program_label}. Check your email for fulfillment details.`,
            'success',
        );
        if (resolve) resolve(redemption);
    } catch (error: any) {
        yield put(redeemPointsFail(error));
        if (reject) reject(error);
        yield call(globalErrorHandler, error);
    }
}

function buildAdminWithdrawalsQuery(params?: IGetAdminLoyaltyWithdrawalsParams): string {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return qs ? `?${qs}` : '';
}

function* getLoyaltyWalletSaga(): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyWalletUrl, httpRequest.GET, null);
        yield put(getLoyaltyWalletSuccess(response.data));
    } catch (error: any) {
        yield put(getLoyaltyWalletFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* requestLoyaltyWithdrawalSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IBuzzbridgeLoyaltyWithdrawPayload;
    resolve?: (v: IBuzzbridgeLoyaltyWithdrawResult) => void;
    reject?: (e: unknown) => void;
}): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyWithdrawUrl, httpRequest.POST, payload);
        const result: IBuzzbridgeLoyaltyWithdrawResult = response.data?.withdrawal
            ? response.data
            : { withdrawal: response.data };
        yield put(requestLoyaltyWithdrawalSuccess(result));
        yield put(getLoyaltyWallet());
        yield put(getLoyaltyDashboard());
        yield call(showToastMessage, 'Withdrawal request submitted for review', 'success');
        if (resolve) resolve(result);
    } catch (error: any) {
        yield put(requestLoyaltyWithdrawalFail(error));
        if (reject) reject(error);
        yield call(globalErrorHandler, error);
    }
}

function* getAdminLoyaltyWithdrawalsSaga({
    payload,
}: {
    payload?: IGetAdminLoyaltyWithdrawalsParams;
}): Generator<any, any, any> {
    try {
        const url = `${buzzbridgeLoyaltyAdminWithdrawalsUrl}${buildAdminWithdrawalsQuery(payload)}`;
        const response = yield call(api, url, httpRequest.GET, null);
        yield put(getAdminLoyaltyWithdrawalsSuccess(response.data));
    } catch (error: any) {
        yield put(getAdminLoyaltyWithdrawalsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* patchAdminLoyaltyWithdrawalSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IPatchAdminLoyaltyWithdrawalArgs;
    resolve?: (v: unknown) => void;
    reject?: (e: unknown) => void;
}): Generator<any, any, any> {
    try {
        const response = yield call(
            api,
            buzzbridgeLoyaltyAdminWithdrawalUrl(payload.withdrawalId),
            httpRequest.PATCH,
            payload.payload,
        );
        const withdrawal = response.data?.withdrawal ?? response.data;
        yield put(patchAdminLoyaltyWithdrawalSuccess(withdrawal));
        yield call(showToastMessage, `Withdrawal marked ${payload.payload.status}`, 'success');
        if (resolve) resolve(withdrawal);
    } catch (error: any) {
        yield put(patchAdminLoyaltyWithdrawalFail(error));
        if (reject) reject(error);
        yield call(globalErrorHandler, error);
    }
}

function buildAdminLeaderboardQuery(params?: IGetAdminLoyaltyLeaderboardParams): string {
    const q = new URLSearchParams();
    if (params?.sort_by) q.set('sort_by', params.sort_by);
    if (params?.search) q.set('search', params.search);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return qs ? `?${qs}` : '';
}

function* getAdminLoyaltyOverviewSaga(): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyAdminOverviewUrl, httpRequest.GET, null);
        const data = response.data?.data ?? response.data;
        yield put(getAdminLoyaltyOverviewSuccess(data));
    } catch (error: any) {
        yield put(getAdminLoyaltyOverviewFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getAdminLoyaltyLeaderboardSaga({
    payload,
}: {
    payload?: IGetAdminLoyaltyLeaderboardParams;
}): Generator<any, any, any> {
    try {
        const url = `${buzzbridgeLoyaltyAdminLeaderboardUrl}${buildAdminLeaderboardQuery(payload)}`;
        const response = yield call(api, url, httpRequest.GET, null);
        const data = response.data?.data ?? response.data;
        yield put(getAdminLoyaltyLeaderboardSuccess(normalizeAdminLoyaltyLeaderboardResponse(data)));
    } catch (error: any) {
        yield put(getAdminLoyaltyLeaderboardFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getAdminLoyaltyUserSaga({ payload: userId }: { payload: string }): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyAdminUserUrl(userId), httpRequest.GET, null);
        const data = response.data?.data ?? response.data;
        yield put(getAdminLoyaltyUserSuccess(data));
    } catch (error: any) {
        yield put(getAdminLoyaltyUserFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* adminGrantLoyaltySaga({ payload }: { payload: IAdminLoyaltyGrantRequest }): Generator<any, any, any> {
    try {
        const response = yield call(api, buzzbridgeLoyaltyAdminGrantUrl, httpRequest.POST, payload, 0, 0);
        const data = response.data?.data ?? response.data;
        yield put(adminGrantLoyaltySuccess(data));
        const parts: string[] = [];
        if (data?.points_granted) parts.push(`${data.points_granted.toLocaleString()} points`);
        if (data?.bonus_cash_cents) {
            parts.push(
                `${(data.bonus_cash_cents / 100).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                })} bonus cash`,
            );
        }
        yield call(showToastMessage, parts.length ? `Granted ${parts.join(' + ')}` : 'Loyalty grant sent', 'success');
    } catch (error: any) {
        yield put(adminGrantLoyaltyFail(error));
        yield call(globalErrorHandler, error);
    }
}

export default function* buzzbridgeLoyaltySaga() {
    yield all([
        takeLatest(types.GET_LOYALTY_DASHBOARD as any, getLoyaltyDashboardSaga),
        takeLatest(types.GET_LOYALTY_NETWORK as any, getLoyaltyNetworkSaga),
        takeLatest(types.GET_LOYALTY_MEMBER as any, getLoyaltyMemberSaga),
        takeLatest(types.GET_LOYALTY_INVITE as any, getLoyaltyInviteSaga),
        takeLatest(types.GET_REDEMPTION_CATALOG as any, getRedemptionCatalogSaga),
        takeLatest(types.REDEEM_POINTS as any, redeemPointsSaga),
        takeLatest(types.GET_LOYALTY_WALLET as any, getLoyaltyWalletSaga),
        takeLatest(types.REQUEST_LOYALTY_WITHDRAWAL as any, requestLoyaltyWithdrawalSaga),
        takeLatest(types.GET_ADMIN_LOYALTY_WITHDRAWALS as any, getAdminLoyaltyWithdrawalsSaga),
        takeLatest(types.PATCH_ADMIN_LOYALTY_WITHDRAWAL as any, patchAdminLoyaltyWithdrawalSaga),
        takeLatest(types.ADMIN_GRANT_LOYALTY as any, adminGrantLoyaltySaga),
        takeLatest(types.GET_ADMIN_LOYALTY_OVERVIEW as any, getAdminLoyaltyOverviewSaga),
        takeLatest(types.GET_ADMIN_LOYALTY_LEADERBOARD as any, getAdminLoyaltyLeaderboardSaga),
        takeLatest(types.GET_ADMIN_LOYALTY_USER as any, getAdminLoyaltyUserSaga),
    ]);
}
